import React from 'react'
import ReactJsonView from 'react-json-view'
import { withRouter } from 'react-router-dom'
import _ from 'underscore'
import ReactLoading from 'react-loading' // https://github.com/fakiolinho/react-loading
import { Box } from '@planningcenter/tapestry-react'
import pcoUrl from '@planningcenter/url'
import { token } from '@planningcenter/tapestry'

import { ThemeProvider } from '~/components/ThemeProvider'
import CopyButton from '~/components/CopyButton'
import FormInput from './ui/form_input.jsx'
import Headline from './ui/headline.jsx'
import NavLink from './ui/nav_link.jsx'
import Pane from './ui/pane.jsx'
import TextInput from './ui/text_input.jsx'

const isStaff = typeof PCOStaffUrls !== 'undefined'

const defaultPerPage = 25
const defaultOffset = 0
const defaultParams = {
  per_page: '',
  offset: '',
  custom: '',
  include: [],
  querying: [],
}
const debounceTime = 500

const log = window.railsEnv === 'development'
let apiProcessedVersion

const apiRoot = pcoUrl(window.railsEnv)('api')

class console {
  static groupCollapsed(...args) {
    if (log) {
      window.console.groupCollapsed(...args)
    }
  }

  static log(...args) {
    if (log) {
      window.console.log(...args)
    }
  }

  static groupEnd() {
    if (log) {
      window.console.groupEnd()
    }
  }

  static error(...args) {
    if (log) {
      window.console.error(...args)
    }
  }
}

class API {
  static get(url, callback, onError) {
    let headers = API.headers()
    let credentials = 'include'

    if (url.includes('churchcenter')) {
      headers['Authorization'] = `Bearer ${window.devAuthToken}`
      credentials = 'omit'
    }

    fetch(url, { credentials: credentials, headers: headers })
      .then((resp) => {
        apiProcessedVersion = resp.headers.get('X-PCO-API-Processed-As-Version')
        return resp.json()
      })
      .then((resp) => {
        if (resp.errors) {
          onError(resp)
        } else {
          callback(resp)
        }
      })
      .catch((err) => {
        console.error(err)
        onError(err)
      })
  }

  static headers() {
    let headers = {}
    headers['X-PCO-API-Version'] = 'LATEST'
    return headers
  }
}

class Node {
  constructor({
    name,
    beta,
    self,
    children,
    path,
    childrenIds,
    id,
    type,
    style,
  }) {
    this.name = name
    this.beta = beta || false
    this.self = self
    this.children = children
    this.path = path
    this.id = id
    this.type = type
    this.childrenIds = childrenIds
    this.style = style
    this.expanded = false
    this.visited = false
  }
}

class Container extends React.Component {
  constructor(props) {
    super(props)

    const apiVersion = 'v2'

    let nodeTree = [
      new Node({
        name: 'api',
        self: `${apiRoot}/api/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'api', apiVersion],
        style: { backgroundColor: '#007ACC', color: 'white' },
      }),
      new Node({
        name: 'calendar',
        self: `${apiRoot}/calendar/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'calendar', apiVersion],
        style: { backgroundColor: '#CC4832', color: 'white' },
      }),
      new Node({
        name: 'check-ins',
        self: `${apiRoot}/check-ins/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'check-ins', apiVersion],
        style: { backgroundColor: '#876096', color: 'white' },
      }),
      new Node({
        name: 'current',
        self: `${apiRoot}/current/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'current', apiVersion],
        style: { backgroundColor: '#007ACC', color: 'white' },
      }),
      new Node({
        name: 'giving',
        self: `${apiRoot}/giving/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'giving', apiVersion],
        style: { backgroundColor: '#F4C551', color: 'white' },
      }),
      new Node({
        name: 'groups',
        self: `${apiRoot}/groups/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'groups', apiVersion],
        style: { backgroundColor: '#FB7642', color: 'white' },
      }),
      new Node({
        name: 'people',
        self: `${apiRoot}/people/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'people', apiVersion],
        style: { backgroundColor: '#4278E3', color: 'white' },
      }),
      new Node({
        name: 'publishing',
        self: `${apiRoot}/publishing/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'publishing', apiVersion],
        style: { backgroundColor: '#454B57', color: 'white' },
      }),
      new Node({
        name: 'registrations',
        self: `${apiRoot}/registrations/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'registrations', apiVersion],
        style: { backgroundColor: '#46948D', color: 'white' },
      }),
      new Node({
        name: 'services',
        self: `${apiRoot}/services/${apiVersion}`,
        children: [],
        childrenIds: [],
        path: [apiRoot, 'services', apiVersion],
        style: { backgroundColor: '#6A9639', color: 'white' },
      }),
    ]

    if (window.railsEnv === 'development') {
      nodeTree = [
        ...nodeTree,
        new Node({
          name: 'cc - calendar',
          self: `http://api.churchcenter.test/calendar/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'calendar', apiVersion],
        }),
        new Node({
          name: 'cc - check-ins',
          self: `http://api.churchcenter.test/check-ins/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'check-ins', apiVersion],
        }),
        new Node({
          name: 'cc - giving',
          self: `http://api.churchcenter.test/giving/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'giving', apiVersion],
        }),
        new Node({
          name: 'cc - groups',
          self: `http://api.churchcenter.test/groups/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'groups', apiVersion],
        }),
        new Node({
          name: 'cc - people',
          self: `http://api.churchcenter.test/people/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'people', apiVersion],
        }),
        new Node({
          name: 'cc - publishing',
          self: `http://api.churchcenter.test/publishing/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'publishing', apiVersion],
        }),
        new Node({
          name: 'cc - services',
          self: `http://api.churchcenter.test/services/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'services', apiVersion],
        }),
        new Node({
          name: 'cc - registrations',
          self: `http://api.churchcenter.test/registrations/${apiVersion}`,
          children: [],
          childrenIds: [],
          path: ['http://api.churchcenter.test', 'registrations', apiVersion],
        }),
      ]
    }

    this.state = {
      tree: { children: nodeTree },
      baseUrl: apiRoot,
      response: {},
      currentNode: null,
      currentURL: '',
      params: defaultParams,
      isFetching: false,
      selectedId: null,
      jsonCollapsedLevel: 2,
    }

    this.handleNodeSelect = this.handleNodeSelect.bind(this)
    this.handleOrderingChange = this.handleOrderingChange.bind(this)
    this.handleQueryingChange = _.debounce(
      this.handleQueryingChange.bind(this),
      debounceTime
    )
    this.handleIncludingChange = this.handleIncludingChange.bind(this)
    this.handleFilteringChange = this.handleFilteringChange.bind(this)
    this.handleLimitingChange = _.debounce(
      this.handleLimitingChange.bind(this),
      debounceTime
    )
    this.handleCustomChange = _.debounce(
      this.handleCustomChange.bind(this),
      debounceTime
    )
    this.handlePrivateApisChange = this.handlePrivateApisChange.bind(this)
    this.handleIDChange = this.handleIDChange.bind(this)
    this.currentURLWithExtraParams = this.currentURLWithExtraParams.bind(this)
    this.updateParams = this.updateParams.bind(this)
    this.createChildren = this.createChildren.bind(this)
    this.findNodeBySelf = this.findNodeBySelf.bind(this)
    this.baseUrl = this.baseUrl.bind(this)
    this.computePath = this.computePath.bind(this)
    this.copyToClipboard = this.copyToClipboard.bind(this)
  }

  componentDidMount() {
    window.console.log(
      '%cHi there! You seem interested in how things work. Want to work at Planning Center? https://planning.center/careers',
      'color: blue'
    )

    const initialPath = this.props.match.params[0]
    const initialNode = !initialPath
      ? null
      : this.state.tree.children.find((node) => {
          const path = `${node.path[1]}/${node.path[2]}`
          return initialPath.match(new RegExp(path))
        })

    if (initialNode) {
      const urlSearchParams = new URLSearchParams(window.location.search)
      const queryParams = Object.fromEntries(urlSearchParams.entries())

      const initialParams = {}
      Object.keys(queryParams).forEach((key) => {
        const value = queryParams[key]

        if (
          key.match(/^(offset|order|per_page|include_private|where\[\w+\])$/)
        ) {
          initialParams[key] = value
        } else if (key.match(/^(filter|include)$/)) {
          initialParams[key] = value.split(',')
        } else {
          initialParams['custom'] = [
            ...(initialParams['custom']?.split('&') || []),
            `${key}=${value}`,
          ].join('&')
        }
      })

      this.handleNodeSelect(initialNode, {
        params: initialParams,
        path: initialPath,
      })
    }
  }

  render() {
    const {
      currentURL,
      currentNode,
      response,
      selectedId,
      tree,
      jsonCollapsedLevel,
    } = this.state

    return (
      <ThemeProvider>
        <Box
          css={{
            display: 'flex',
            overflow: 'hidden',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            css={{
              display: 'flex',
              flex: '1',
              paddingBottom: '32px',
            }}
          >
            <Box css={{ flexBasis: '200px' }}>
              <Tree
                parent={tree}
                children={tree.children}
                onClick={this.handleNodeSelect}
                onCaretClick={(node) => {
                  node.expanded = !node.expanded
                  this.setState({})
                }}
                currentURL={this.baseUrl()}
                currentNode={currentNode}
              />
              <Box css={{ padding: '2em 1.4em' }}>
                <a
                  href="https://planning.center/2017/introducing-the-api-explorer/"
                  target="_blank"
                  rel="noreferrer"
                >
                  about the API Explorer
                </a>
              </Box>
            </Box>
            <Box
              css={{
                background: token('--t-surface-color-canvas'),
                flex: '1',
                minWidth: '0',
                padding: '0 0 32px 32px',
              }}
            >
              <Pane
                css={{
                  alignItems: 'flex-start',
                  background: token('--t-surface-color-card'),
                  display: 'flex',
                  lineHeight: '24px',
                  marginBottom: '32px',
                  minHeight: '24px',
                }}
              >
                <Box css={{ flex: '0 0 auto', fontWeight: '700' }}>
                  Current URL:
                </Box>
                <Box
                  css={{
                    color: token('--t-form-color-readonly'),
                    margin: '0 8px 0 16px',
                    padding: '0 8px',
                    minWidth: '0',
                    wordWrap: 'break-word',
                    borderRadius: token('--t-form-border-radius'),
                    background: currentURL
                      ? token('--t-form-background-color-readonly')
                      : 'none',
                  }}
                >
                  {currentURL ? currentURL : 'none selected'}
                </Box>
                {currentURL && (
                  <CopyButton copyValue={currentURL} iconOnly={false} />
                )}
              </Pane>
              <Box
                css={{
                  display: 'flex',
                  flex: '1',
                  '@media(max-width: 1000px)': {
                    flexDirection: 'column',
                  },
                }}
              >
                <Box
                  css={{
                    flex: '0 4 400px',
                    margin: '0 32px 0 0',
                    minWidth: '0',
                    overflow: 'hidden',
                    textOverflow: 'ellipses',
                    whiteSpace: 'nowrap',
                    '@media(max-width: 1000px)': {
                      flex: '1',
                      margin: '0 0 32px',
                    },
                  }}
                >
                  <Headline>URL Parameters</Headline>
                  {isStaff && (
                    <ViewPrivateApis
                      {...this.state}
                      onChange={this.handlePrivateApisChange}
                    />
                  )}
                  <ID
                    parent={this.recursivelyFindByPath(
                      this.computePath(currentURL).slice(0, -1),
                      tree
                    )}
                    currentNode={currentNode}
                    selectedId={selectedId}
                    onChange={this.handleIDChange}
                  />
                  <Ordering
                    {...this.state}
                    onChange={this.handleOrderingChange}
                  />
                  <Querying
                    {...this.state}
                    onChange={(e) => {
                      e.persist()
                      this.handleQueryingChange(e)
                    }}
                  />
                  <Including
                    {...this.state}
                    onChange={(e) => this.handleIncludingChange(e)}
                  />
                  <Filtering
                    {...this.state}
                    onChange={(e) => this.handleFilteringChange(e)}
                  />
                  {!this.isResource(currentNode) && (
                    <Limiting
                      {...this.state}
                      onChange={(e) => {
                        e.persist()
                        this.handleLimitingChange(e)
                      }}
                    />
                  )}
                  <Custom
                    {...this.state}
                    onChange={(e) => {
                      e.persist()
                      this.handleCustomChange(e)
                    }}
                  />
                </Box>
                <Box
                  css={{
                    flex: '1 1 400px',
                    minWidth: '0',
                  }}
                >
                  <Headline>Server Response</Headline>
                  {apiProcessedVersion && (
                    <Box
                      css={{
                        clear: 'both',
                        flex: '0 0 auto',
                        fontWeight: '700',
                      }}
                    >
                      API Version: {apiProcessedVersion}
                    </Box>
                  )}
                  <Pane theme="dark" css={{ overflow: 'scroll' }}>
                    {this.state.isFetching ? (
                      <ReactLoading type="cylon" color="#777" delay={0} />
                    ) : (
                      <ReactJsonView
                        collapsed={jsonCollapsedLevel}
                        displayDataTypes={false}
                        src={response}
                        theme="ocean"
                        enableClipboard={(e) => {
                          this.copyToClipboard(e.src)
                        }}
                      />
                    )}
                  </Pane>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    )
  }

  createChildren({ response }) {
    const { data } = response
    const { tree, currentNode } = this.state

    if (data !== undefined) {
      if (data.links) {
        // A single resource

        const children = []
        Object.keys(data.links).map((k) => {
          if (k !== 'self') {
            const path = this.computePath(data.links[k])
            const name = isNaN(Number(path.slice(-1))) ? k : `ID (${k.id})`
            const parent = this.recursivelyFindByPath(path.slice(0, -1), tree)

            if (parent === currentNode) {
              children.push(
                new Node({
                  self: data.links[k],
                  type: k,
                  children: [],
                  id: Number(data.id),
                  name,
                  path,
                })
              )
              parent.children = _.uniq(children, false, (c) => c.path.join(','))
            }
          }
        })
      } else if (Array.isArray(data)) {
        // a collection of resources

        const d = data[0]
        if (d !== undefined) {
          const path = this.computePath(response.links.self)
          const parent = this.recursivelyFindByPath(
            this.computePath(response.links.self),
            tree
          )

          if (d.id) {
            path.push(d.id)
          }

          const node = new Node({
            self: d.links.self,
            type: d.type,
            children: [],
            id: Number(d.id),
            name: 'ID',
            path,
          })

          if (parent) {
            parent.children = [node]
            parent.childrenIds = data.map((d) => d.id)
          }
        }
      }
    }
  }

  handleNodeSelect(currentNode, options = {}) {
    let selectedId
    let params = { include: [], querying: [], filtering: [] }

    if (this.isResource(currentNode)) {
      selectedId = currentNode.path.slice(-1)[0]
    } else {
      selectedId = null
    }

    currentNode.visited = true

    if (!currentNode.expanded) {
      currentNode.expanded = true
    }

    if (`/${currentNode.path.slice(1).join('/')}` === options.path) {
      params = { ...params, ...options.params }
    }

    this.setState(
      {
        currentNode,
        selectedId,
        currentURL: currentNode.self,
      },
      () => {
        this.updateParams(params, { path: options.path }, () => {
          if (options.path && this.state.currentNode.children) {
            let nextNode

            // deep-ish clone
            const currentNode = {
              ...this.state.currentNode,
              children: [...this.state.currentNode.children],
            }

            const currentPath = currentNode.self.substring(apiRoot.length)
            const nextPathComponent = options.path
              .substring(currentPath.length + 1)
              .split('/')
              .at(0)
            const idMatch = nextPathComponent.match(/^\d+$/)
            const id = idMatch ? idMatch[0] : null

            nextNode = currentNode.children.find((node) => {
              const path = `${node.path.slice(1).join('/')}`
              return options.path.match(new RegExp(path))
            })

            if (!nextNode && id) {
              // The path doesn't match the existing child resource returned
              // from the API (actually, it doesn't match the child resource
              // returned from createChildren(), but close enough),
              // so we need to add it.
              const url = `${currentNode.self}/${id}`
              nextNode = currentNode.children.at(0)
              const path = url.replace(/^https?:\/\//, '').split('/')
              if (nextNode) {
                // Update the existing ID node in-place with the details we want.
                nextNode.id = Number(id)
                nextNode.self = url
                nextNode.path = path
              } else {
                // No existing ID node was available, so we have to create a new one.
                nextNode = new Node({
                  self: url,
                  id: Number(id),
                  name: 'ID',
                  children: [],
                  path,
                })
                currentNode.children.push(nextNode)
              }
              this.setState({ currentNode })
            }

            if (nextNode) {
              this.handleNodeSelect(nextNode, options)
            }
          }
        })
      }
    )
  }

  handleOrderingChange(e) {
    const value = e.target.value
    let params = this.state.params

    if (value === 'none') {
      delete params.order
    } else {
      params = Object.assign(params, { order: value })
    }

    this.updateParams(params)
  }

  handleQueryingChange(e) {
    const { name, value } = e.target
    const key = `where[${name}]`
    let params = this.state.params

    if (value === '') {
      delete params[key]
    } else {
      params = Object.assign(params, { [key]: value })
    }

    this.updateParams(params)
  }

  handleIncludingChange(e) {
    const { name, checked } = e.target
    let params = this.state.params
    let included = params.include || []

    if (checked) {
      included.push(name)
    } else {
      included = included.filter((i) => i !== name)
    }

    params = Object.assign(params, {
      include: included,
    })

    this.updateParams(params)
  }

  handleFilteringChange(e) {
    const { name, checked } = e.target
    let params = this.state.params
    let filtered = params.filter || []

    if (checked) {
      filtered.push(name)
    } else {
      filtered = filtered.filter((i) => i !== name)
    }

    params = Object.assign(params, {
      filter: filtered,
    })

    this.updateParams(params)
  }

  handleLimitingChange(e) {
    let { name, value } = e.target
    if (name == 'offset' && value == '') {
      value = defaultOffset
    }
    if (name == 'per_page' && value == '') {
      value = defaultPerPage
    }
    let params = this.state.params
    params = Object.assign(params, { [name]: value })
    this.updateParams(params)
  }

  handleCustomChange(e) {
    let params = this.state.params
    params = Object.assign(params, { custom: e.target.value })
    this.updateParams(params)
  }

  handlePrivateApisChange() {
    let params = this.state.params
    const including = params.include_private
    if (including) {
      delete params.include_private
      this.updateParams(params)
    } else {
      this.updateParams({ ...params, include_private: true })
    }
  }

  handleIDChange(e) {
    const id = e.target.value
    const { currentNode } = this.state
    const re = new RegExp(`${currentNode.id}$`)
    currentNode.self = currentNode.self.replace(re, id)
    currentNode.id = id
    currentNode.path.pop()
    currentNode.path.push(id)

    currentNode.children.forEach((c) => {
      c.path[c.path.length - 2] = id
      const re = new RegExp(`\\d+/${c.name}$`)
      c.self = c.self.replace(re, `${id}/${c.name}`)
    })

    this.setState({ selectedId: id, currentNode })
    this.updateParams(this.state.params)
  }

  findNodeBySelf(link) {
    return this.state.tree.children.find((n) => n.self === link)
  }

  updateParams(params, options = {}, callback = () => {}) {
    this.setState({ params, isFetching: true }, () => {
      const currentURL = this.currentURLWithExtraParams()

      if (!options.path) {
        const newUrl = currentURL.replace(apiRoot, '/explorer')
        window.history.pushState({}, '', newUrl)
      }

      API.get(
        currentURL,
        (response) => {
          let tree = this.state.tree
          this.createChildren({ response })
          this.setState(
            { currentURL, response, tree, isFetching: false },
            callback
          )
        },
        (error) => {
          this.setState({ currentURL, response: error, isFetching: false })
        }
      )
    })
  }

  recursivelyFindByPath(pathNeedle, nodeHaystack) {
    let match
    nodeHaystack.children.some((c) => {
      const pathString = c.path.toString().replace(/https?:\/\//, '')
      if (pathNeedle.toString() == pathString) {
        match = c
        return true
      } else {
        if (c.children.length > 0) {
          match = this.recursivelyFindByPath(pathNeedle, c)
        }
      }
      return match
    })
    return match
  }

  currentURLWithExtraParams(extraParams = {}) {
    const base = this.baseUrl()
    let params = Object.assign(this.state.params, extraParams)
    if (params.per_page == defaultPerPage) {
      delete params.per_page
    }
    if (params.offset == defaultOffset) {
      delete params.offset
    }
    if (params.custom == '') {
      delete params.custom
    }

    ;['include', 'querying', 'filtering'].forEach((p) => {
      if (params[p] && params[p].length < 1) {
        delete params[p]
      }
    })

    let newParams = Object.keys(params)
      .filter((p) => p !== 'custom')
      .map((k) => `${k}=${params[k]}`)
    params.custom && newParams.push(params.custom)
    newParams = newParams.join('&')
    if (newParams.length > 0) {
      return `${base}?${newParams}`
    } else {
      return base
    }
  }

  baseUrl() {
    const { currentNode, selectedId } = this.state
    if (this.isResource(currentNode)) {
      return currentNode.self.replace(/\/\d+$/, `/${selectedId}`)
    } else {
      return currentNode && currentNode.self
    }
  }

  isResource(node) {
    if (node === null) {
      return false
    } else {
      return node.path && node.path.slice(-1)[0].match(/^\d+$/)
    }
  }

  computePath(link) {
    if (link === null) {
      return []
    }
    return link.replace(/https?:\/\//, '').split('/')
  }

  async copyToClipboard(text) {
    let copyValue

    if (_.isString(text)) {
      copyValue = text
    } else {
      copyValue = JSON.stringify(text, null, '  ')
    }

    // clipboard api is not available in insecure contexts (e.g. dev env)
    // https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(copyValue)
    } else {
      const el = document.createElement('textarea')
      el.value = copyValue
      document.body.appendChild(el)
      el.select()
      // execCommand() is deprecated, but we'll use it when the clipboard api
      // is unavailable
      document.execCommand('copy')
      document.body.removeChild(el)
    }
  }
}

const Tree = ({ children, currentURL, currentNode, onClick, onCaretClick }) => {
  const tree = children.map((l) => {
    let childTree
    if (l.children.length > 0 && l.expanded) {
      childTree = (
        <Tree
          parent={l}
          key={`${l.self}-children`}
          children={l.children}
          onClick={onClick}
          onCaretClick={onCaretClick}
          currentURL={currentURL}
          currentNode={currentNode}
        />
      )
    }

    const inCurrentPath = (node) => {
      return node.path.every((segment, index) => {
        return currentNode.path[index] === segment
      })
    }

    const betaDisplay = l.beta ? ' (beta)' : ''
    const displayName = `${l.name}${betaDisplay}`
    const display = l.name == 'ID' ? `ID (${l.id})` : displayName

    return (
      <div key={l.self} style={{}}>
        <NavLink
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClick(l)
          }}
          key={l.self}
          visited={l.visited}
          selected={currentURL === l.self}
          level={l.path.length - 2}
          style={l.style}
          expanded={l.expanded}
          hasChildren={l.children.length > 0}
          inCurrentPath={currentNode && inCurrentPath(l)}
          onCaretClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onCaretClick(l)
          }}
        >
          {display}
        </NavLink>
        {childTree}
      </div>
    )
  })

  return <div>{tree}</div>
}

class ID extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      selectType: 'discovered',
    }
  }

  render() {
    const { onChange, parent, currentNode } = this.props
    const { selectType } = this.state

    if (parent && parent.childrenIds && parent.childrenIds.length > 0) {
      const options = parent.childrenIds.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))

      return (
        <Pane>
          <Headline level={2}>{currentNode.type} ID</Headline>
          <label htmlFor="discovered">
            <input
              type="radio"
              name="id_radio"
              id="discovered"
              value="discovered"
              checked={selectType === 'discovered'}
              onChange={() => this.setState({ selectType: 'discovered' })}
            />{' '}
            Discovered
          </label>
          <label htmlFor="custom">
            <input
              type="radio"
              name="id_radio"
              id="custom"
              value="custom"
              checked={selectType === 'custom'}
              onChange={() => this.setState({ selectType: 'custom' })}
            />{' '}
            Custom
          </label>

          <div className="mt-1r">
            {selectType === 'discovered' && (
              <select onChange={onChange} value={currentNode.id}>
                {options}
              </select>
            )}

            {selectType === 'custom' && (
              <input
                type="number"
                defaultValue={currentNode.id}
                onChange={onChange}
              />
            )}
          </div>
        </Pane>
      )
    } else {
      return null
    }
  }
}

const Ordering = ({ response, onChange, params }) => {
  if (response && response.meta && response.meta.can_order_by) {
    const options = response.meta.can_order_by.map((o) => (
      <FormInput
        id={`order-${o}`}
        key={`order-${o}`}
        type="radio"
        name="orderBy"
        value={o}
        checked={params.order === o}
        selected={params.order === o}
        onChange={onChange}
      >
        {o}
      </FormInput>
    ))

    return (
      <Pane>
        <Headline level={2}>Ordering</Headline>
        <div>
          <FormInput
            id={`ordering-none`}
            key="ordering-none"
            type="radio"
            name="orderBy"
            onChange={onChange}
            value="none"
            checked={params.order == undefined}
            selected={!params.order}
          >
            none
          </FormInput>
          {options}
        </div>
      </Pane>
    )
  } else {
    return null
  }
}

const Querying = ({ response, params, onChange }) => {
  if (response && response.meta && response.meta.can_query_by) {
    const options = response.meta.can_query_by.map((o) => (
      <TextInput
        key={`${o}-key`}
        name={o}
        type="text"
        onChange={onChange}
        defaultValue={params[`where[${o}]`]}
      />
    ))

    return (
      <Pane>
        <Headline level={2}>Querying</Headline>
        {options}
      </Pane>
    )
  } else {
    return null
  }
}

const Including = ({ response, onChange, params }) => {
  if (response && response.meta && response.meta.can_include) {
    const options = response.meta.can_include.map((o) => (
      <FormInput
        id={`include-${o}`}
        key={`include-${o}`}
        type="checkbox"
        name={o}
        value={o}
        onChange={onChange}
        checked={params.include && params.include.includes(o)}
        selected={params.include && params.include.includes(o)}
      >
        {o}
      </FormInput>
    ))

    return (
      <Pane>
        <Headline level={2}>Including</Headline>
        <div>{options}</div>
      </Pane>
    )
  } else {
    return null
  }
}

const Filtering = ({ response, onChange, params }) => {
  if (response && response.meta && response.meta.can_filter) {
    const options = response.meta.can_filter.map((o) => (
      <FormInput
        id={`filter-${o}`}
        key={`filter-${o}`}
        type="checkbox"
        name={o}
        value={o}
        onChange={onChange}
        checked={params.filter && params.filter.includes(o)}
        selected={params.filter && params.filter.includes(o)}
      >
        {o}
      </FormInput>
    ))

    return (
      <Pane>
        <Headline level={2}>Filtering</Headline>
        <div>{options}</div>
      </Pane>
    )
  } else {
    return null
  }
}

const Limiting = ({ onChange, params }) => {
  return (
    <Pane>
      <Headline level={2}>Limiting</Headline>
      <TextInput
        onChange={onChange}
        name="offset"
        type="number"
        defaultValue={params.offset}
        placeholder={`default is ${defaultOffset}`}
      >
        Offset:
      </TextInput>
      <TextInput
        onChange={onChange}
        name="per_page"
        type="number"
        defaultValue={params.per_page}
        placeholder={`default is ${defaultPerPage}`}
      >
        Per page:
      </TextInput>
    </Pane>
  )
}

const Custom = ({ onChange, params }) => {
  return (
    <Pane>
      <Headline level={2}>Custom Parameters</Headline>
      <TextInput
        onChange={onChange}
        name="custom"
        type="text"
        defaultValue={params.custom}
        placeholder="param=value&param2=value2"
      >
        Custom:
      </TextInput>
    </Pane>
  )
}

const CurrentLink = ({ current }) => {
  return (
    <Box
      css={{ background: token('--t-surface-color-canvas'), padding: '4px' }}
    >
      <Headline css={{ display: 'flex', margin: '0' }}>
        <span>Current Link</span>
        <input
          style={{
            color: '#979797',
            flex: '1',
            fontSize: '14px',
            lineHeight: '16px',
            marginLeft: '8px',
            padding: '4px',
          }}
          readOnly={true}
          type="text"
          value={current}
        />
      </Headline>
    </Box>
  )
}

const ViewPrivateApis = ({ onChange, params }) => {
  const checked = !!params.include_private

  return (
    <Pane
      css={{ border: '2px solid #ff69b4 !important' }}
      className="staff-only"
    >
      <Headline level={2}>Staff Only</Headline>
      <FormInput selected={checked} onChange={onChange} type="checkbox">
        <div>View Private Apis?</div>
        <sub>{'(pco-api >= 8.1 required)'}</sub>
      </FormInput>
    </Pane>
  )
}

export default withRouter(Container)
